import { addDays, toDate, type DateKey, type MonthKey } from '@kff/engine';
import { QUOTE_TERMS, type QuoteTerms } from './pricing.js';

/**
 * Clients overview (owner 2026-10-16, "Lista och faktaband"): when an agreement binds, when notice must be given,
 * and which clients need attention. Pure date rules, used by the Clients list and the client card.
 */

const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** `k` plus `n` calendar months; a day past the month's end becomes its last day (31 Jan + 1 = 28 Feb). */
export function addMonths(k: DateKey, n: number): DateKey {
  const [y, m, d] = k.split('-').map(Number) as [number, number, number];
  const idx = y * 12 + (m - 1) + n;
  const yy = Math.floor(idx / 12);
  const mm = (idx % 12) + 1;
  return `${yy}-${pad(mm)}-${pad(Math.min(d, lastDay(yy, mm)))}`;
}

/** The last day of `k`'s month. */
export const endOfMonth = (k: DateKey): DateKey => {
  const [y, m] = k.split('-').map(Number) as [number, number];
  return `${y}-${pad(m)}-${pad(lastDay(y, m))}`;
};

export const daysBetween = (from: DateKey, to: DateKey): number => Math.round((toDate(to).getTime() - toDate(from).getTime()) / 86_400_000);

/** How many days before the date that matters the list starts flagging an agreement. */
export const RENEW_WINDOW_DAYS = 90;
/** A client with nothing ongoing and no delivery for this many months counts as quiet. */
export const QUIET_MONTHS = 2;

export type AgreementPhase = 'upcoming' | 'binding' | 'rolling' | 'period' | 'period_over';

export interface AgreementPeriod {
  start: DateKey;
  /** Subscription: last day of the minimum term. Per production: last day of the call-off period. Null = no minimum term. */
  end: DateKey | null;
  /** Subscription: the last day to give notice so the agreement ends when the minimum term ends (frame agreement § Uppsägning). */
  noticeBy: DateKey | null;
  phase: AgreementPhase;
  /** How far today is through start → end, 0–1; null without an end */
  progress: number | null;
  /** The date to act on: notice-by for a subscription, the period's end per production */
  actBy: DateKey | null;
  /** Act within RENEW_WINDOW_DAYS, or a call-off period that has run out */
  renewSoon: boolean;
}

/**
 * A signed agreement's dates. Start = the quote's start date, else the day it was signed.
 * Notice runs from the start of the next calendar month, so notice given in month M ends the agreement at the end of M + notice months.
 */
export function agreementPeriod(t: Pick<QuoteTerms, 'kind' | 'startDate' | 'periodMonths'>, signedOn: DateKey | null, today: DateKey, noticeMonths = QUOTE_TERMS.noticeMonths): AgreementPeriod | null {
  const start = t.startDate ?? signedOn;
  if (!start) return null;
  const months = t.kind === 'project' ? (t.periodMonths ?? 2) : t.periodMonths;
  const end = months ? addDays(addMonths(start, months), -1) : null;
  // the last month whose notice still ends it by `end`: notice in month M ends at the end of M + notice months
  const noticeBy = t.kind === 'subscription' && end ? endOfMonth(addMonths(`${addDays(end, 1).slice(0, 7)}-01`, -noticeMonths - 1)) : null;
  const phase: AgreementPhase = today < start ? 'upcoming' : t.kind === 'project' ? (end && today > end ? 'period_over' : 'period') : end && today <= end ? 'binding' : 'rolling';
  const progress = end ? Math.min(1, Math.max(0, daysBetween(start, today) / Math.max(1, daysBetween(start, end)))) : null;
  const actBy = t.kind === 'subscription' ? noticeBy : end;
  const renewSoon = Boolean(actBy) && (phase === 'period_over' || (actBy! >= today && daysBetween(today, actBy!) <= RENEW_WINDOW_DAYS));
  return { start, end, noticeBy, phase, progress, actBy, renewSoon };
}

/** A month key `n` months before `m`. */
export const monthsBefore = (m: MonthKey, n: number): MonthKey => addMonths(`${m}-01`, -n).slice(0, 7);
